import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const VALUES = [
  {
    title: 'Research Integrity',
    body: 'Every product is sold exclusively for in-vitro laboratory and research use. We verify purchaser eligibility and enforce strict research-only terms — no exceptions.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.955 11.955 0 01.75 12c0 6.627 5.373 12 12 12s12-5.373 12-12c0-2.717-.904-5.222-2.428-7.175" />
      </svg>
    ),
  },
  {
    title: 'Third-Party Verified',
    body: 'Every batch is tested by accredited independent laboratories. Certificates of Analysis are available on request and include HPLC purity data and mass-spec confirmation.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z" />
      </svg>
    ),
  },
  {
    title: 'Quality Standards',
    body: 'We operate under research-chemical compliance guidelines and ship domestically from our fulfillment centre. All orders are subject to strict data protection.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582" />
      </svg>
    ),
  },
  {
    title: 'Transparent Pricing',
    body: 'Honest, upfront pricing with no hidden fees. What you see at checkout is what you pay — items and shipping, with any coupon applied clearly on your order summary.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
      </svg>
    ),
  },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="bg-tea-700 text-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-300 text-sm font-semibold uppercase tracking-widest mb-4">About Aloha Amino</p>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight max-w-2xl">
            Research peptides. Rigorous science. Transparent pricing.
          </h1>
          <p className="mt-6 text-tea-200 text-lg md:text-xl max-w-2xl leading-relaxed">
            Aloha Amino Research Inc. is a supplier of research-grade peptides to licensed
            laboratories, postdoctoral researchers, and qualified lab providers.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
              Supplying the tools researchers actually need.
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed">
              Reagent budgets are tight. Aloha Amino was built around a straightforward idea: supply
              verified labs with research-grade peptides at honest prices, while keeping quality
              high and margins transparent.
            </p>
            <p className="mt-4 text-gray-600 text-lg leading-relaxed">
              Every batch ships with a third-party Certificate of Analysis, fulfilled domestically
              in 1–3 business days, so labs get exactly what they ordered — on time and as specified.
            </p>
            <div className="mt-8 flex gap-4 flex-wrap">
              <Link to="/shop" className="btn-primary">Browse Catalog</Link>
              <Link to="/register" className="btn-secondary">Create an Account</Link>
            </div>
          </div>
          <div className="bg-tea-50 rounded-2xl p-10 flex flex-col gap-5">
            {[
              { stat: '≥ 98%', label: 'HPLC purity guarantee' },
              { stat: 'COA', label: 'Available for every batch' },
              { stat: '24/7', label: 'Order tracking & support' },
              { stat: '1–3 day', label: 'Domestic fulfillment' },
            ].map(({ stat, label }) => (
              <div key={label} className="flex items-center gap-4">
                <span className="text-tea-700 font-extrabold text-2xl w-28 shrink-0">{stat}</span>
                <span className="text-gray-600">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">What we stand for</h2>
          <div className="grid sm:grid-cols-2 gap-8">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-tea-100 text-tea-700">
                  {v.icon}
                </span>
                <h3 className="mt-5 font-bold text-gray-900 text-lg">{v.title}</h3>
                <p className="mt-2 text-gray-600 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance notice */}
      <section className="py-12 bg-amber-50 border-y border-amber-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-amber-900 text-sm leading-relaxed max-w-3xl mx-auto">
            <strong>⚠️ For laboratory research use only.</strong> All products are sold exclusively for
            in-vitro and ex-vivo research purposes. They are not drugs, foods, cosmetics, or medical
            devices, and have not been evaluated or approved by the FDA or any other
            regulatory authority for human or veterinary use. Purchasers must be qualified researchers
            or licensed laboratory providers.
          </p>
          <Link to="/compliance" className="mt-4 inline-block text-amber-800 underline text-sm hover:text-amber-900">
            Read our full research use disclosure →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to order?</h2>
          <p className="text-gray-600 mb-8">Verify your lab credentials and start building your research supply chain.</p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/register" className="btn-primary">Create Account</Link>
            <Link to="/support" className="btn-secondary">Contact Lab Support</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
